import type { Expression, Filter } from '@fabric-ui/components';
import { isExpr } from './filter-utils';

export type ExpressionFilter = Filter & { expression: Expression };

/**
 * Maps every expression leaf while preserving logical operators, metadata, and unchanged branch references.
 *
 * @param source - The filter tree to transform.
 * @param mapper - The transformation applied to each expression leaf.
 * @returns The transformed filter tree, or the original reference when no leaf changes.
 */
export const mapFilterLeaves = (source: Filter, mapper: (leaf: ExpressionFilter) => Filter): Filter => {
  if (isExpr(source)) return mapper(source);

  if (source.not) {
    const not = mapFilterLeaves(source.not, mapper);
    return not === source.not ? source : { ...source, not };
  }

  const logical = source.and ? (['and', source.and] as const) : source.or ? (['or', source.or] as const) : null;
  if (!logical) return source;

  const [operator, condition] = logical;
  const operands = condition.operands.map(operand => mapFilterLeaves(operand, mapper));
  const changed = operands.some((operand, index) => operand !== condition.operands[index]);
  return changed ? ({ ...source, [operator]: { ...condition, operands } } as Filter) : source;
};
